import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, RefreshCw, AlertCircle, Filter } from 'lucide-react';
import { useWhatsAppConversations } from '../../hooks/useWhatsAppConversations';
import ConversationList from '../../components/whatsapp/ConversationList';
import ChatWindow from '../../components/whatsapp/ChatWindow';
import { useSidebar } from '../../contexts/SidebarContext';

const FILTERS = [
  { id: 'all',      label: 'Toutes' },
  { id: 'active',   label: 'Actives' },
  { id: 'pending',  label: 'En attente' },
  { id: 'archived', label: 'Archivées' },
];

const safeIncludes = (value, query) =>
  typeof value === 'string' && value.toLowerCase().includes(query);

function WhatsAppLogo({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.978-1.41A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z"
        fill="currentColor"
        fillOpacity="0.18"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M8.5 9.5c.167-.5.5-1 1-1.2.4-.16.7-.1.9.1l.8 1.1c.2.25.15.55-.05.75l-.5.5c-.1.1-.1.25 0 .35.3.45.75.95 1.2 1.3.45.35.9.65 1.3.8.15.05.3 0 .4-.1l.5-.5c.2-.2.5-.25.75-.05l1.1.8c.2.2.25.5.1.9-.2.5-.7.85-1.2 1-.9.25-2.55-.1-4.2-1.75C8.6 12.05 8.25 10.4 8.5 9.5z"
        fill="currentColor"
      />
    </svg>
  );
}

export default function WhatsAppPage() {
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [mobileView, setMobileView] = useState('list');

  const { isExpanded, isMobileMenuOpen } = useSidebar();
  const [isMobileLayout, setIsMobileLayout] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobileLayout(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Même unité que Tailwind : w-72 = 18rem, w-20 = 5rem + même easing que la sidebar
  const sidebarLeft = isMobileLayout ? '0px' : isExpanded ? '18rem' : '5rem';

  const { conversations, loading, error, refetch, unreadCounts, clearUnread } =
    useWhatsAppConversations();

  const handleSelect = useCallback(
    (conv) => {
      setSelected(conv);
      clearUnread(conv._id);
      setMobileView('chat');
    },
    [clearUnread]
  );

  const handleClose = useCallback(() => {
    setMobileView('list');
    if (window.innerWidth >= 768) setSelected(null);
  }, []);

  useEffect(() => {
    if (!selected) return;
    const selectedId = String(selected._id);
    const fresh = conversations.find((c) => String(c._id) === selectedId);
    if (fresh && fresh !== selected) setSelected(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations]);

  const filtered = conversations.filter((conv) => {
    if (filter !== 'all' && conv.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return safeIncludes(conv.clientName, q) || safeIncludes(conv.clientPhone, q);
    }
    return true;
  });

  const totalUnread = Object.values(unreadCounts).reduce((s, n) => s + (n || 0), 0);
  const activeCount  = conversations.filter(c => c.status === 'active').length;

  return (
    <div
      className="flex overflow-hidden"
      style={{
        position: 'fixed',
        top: 0,
        left: sidebarLeft,
        right: 0,
        bottom: 0,
        transition: 'left 0.3s ease-in-out',
        background: 'var(--c-primary-dark)',
        zIndex: 10,
      }}
    >
      {/* ── Panneau gauche — liste des conversations ──────────────────────── */}
      <div
        className={`flex flex-col flex-shrink-0 ${
          mobileView === 'chat' ? 'hidden md:flex' : 'flex'
        } w-full md:w-[340px]`}
        style={{ background: 'var(--c-primary-card)', borderRight: '0.5px solid var(--c-border)' }}
      >
        {/* ── En-tête ──────────────────────────────────────────────────────── */}
        <div className="p-4 pb-3" style={{ borderBottom: '0.5px solid var(--c-border)' }}>

          {/* Titre + badge non-lus + refresh */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div
                className="relative w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(var(--c-accent-green-rgb), 0.13)', color: 'var(--c-accent-green)' }}
              >
                <WhatsAppLogo size={22} />
                {totalUnread > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
                    {totalUnread > 99 ? '99+' : totalUnread}
                  </span>
                )}
              </div>
              <div>
                <h2 className="text-sm font-semibold text-text-light leading-tight">
                  Messages WhatsApp
                </h2>
                <p className="text-[11px] text-text-gray mt-0.5">
                  {activeCount > 0
                    ? `${activeCount} conversation${activeCount > 1 ? 's' : ''} active${activeCount > 1 ? 's' : ''}`
                    : 'Aucune conversation active'}
                </p>
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              onClick={refetch}
              disabled={loading}
              className="p-2 rounded-xl transition-colors disabled:opacity-40"
              style={{ color: 'var(--c-text-gray)' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--c-accent-green)'; e.currentTarget.style.background = 'var(--c-hover)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--c-text-gray)'; e.currentTarget.style.background = 'transparent'; }}
              title="Rafraîchir"
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            </motion.button>
          </div>

          {/* Barre de recherche */}
          <div className="relative mb-3 group">
            <Search
              className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none transition-colors"
              size={14}
              style={{ color: 'var(--c-text-gray)' }}
            />
            <input
              type="text"
              placeholder="Rechercher un contact…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full text-sm pl-9 pr-3 py-2.5 rounded-xl outline-none transition-all"
              style={{
                background: 'var(--c-primary-dark)',
                border: '0.5px solid var(--c-border)',
                color: 'var(--c-text-light)',
              }}
              onFocus={e => { e.target.style.borderColor = 'rgba(var(--c-accent-green-rgb), 0.45)'; }}
              onBlur={e => { e.target.style.borderColor = 'var(--c-border)'; }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-gray hover:text-text-light transition-colors text-xs"
              >
                ✕
              </button>
            )}
          </div>

          {/* Filtres — pills */}
          <div className="flex items-center gap-1.5">
            <Filter size={11} style={{ color: 'var(--c-text-gray)', flexShrink: 0 }} />
            <div className="flex gap-1.5 flex-wrap">
              {FILTERS.map((f) => {
                const isActive = filter === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    className="px-3 py-1 rounded-full text-xs font-semibold transition-all"
                    style={
                      isActive
                        ? { background: 'var(--c-accent-green)', color: '#fff' }
                        : { color: 'var(--c-text-gray)', background: 'transparent' }
                    }
                    onMouseEnter={e => {
                      if (!isActive) {
                        e.currentTarget.style.background = 'var(--c-hover)';
                        e.currentTarget.style.color = 'var(--c-text-light)';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isActive) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = 'var(--c-text-gray)';
                      }
                    }}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Contenu : chargement / erreur / liste ───────────────────────── */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div
              className="w-8 h-8 rounded-full border-2 border-transparent animate-spin"
              style={{ borderTopColor: 'var(--c-accent-green)' }}
            />
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center">
              <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-text-gray text-sm mb-3">{error}</p>
              <button
                onClick={refetch}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-xs rounded-xl transition-colors"
              >
                Réessayer
              </button>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
              style={{ background: 'rgba(var(--c-accent-green-rgb), 0.08)', color: 'var(--c-accent-green)' }}
            >
              <WhatsAppLogo size={26} />
            </div>
            <p className="text-text-light text-sm font-medium mb-1">
              {search ? 'Aucun résultat' : 'Aucune conversation'}
            </p>
            <p className="text-text-gray text-xs">
              {search
                ? 'Essayez un autre terme de recherche'
                : filter !== 'all'
                ? 'Aucune conversation dans cette catégorie'
                : 'Les conversations WhatsApp apparaîtront ici'}
            </p>
          </div>
        ) : (
          <ConversationList
            conversations={filtered}
            selectedId={selected?._id}
            onSelect={handleSelect}
            unreadCounts={unreadCounts}
          />
        )}
      </div>

      {/* ── Fenêtre de chat ──────────────────────────────────────────────── */}
      <div
        className={`flex-1 overflow-hidden ${
          mobileView === 'list' ? 'hidden md:flex' : 'flex'
        }`}
      >
        <ChatWindow conversation={selected} onClose={handleClose} />
      </div>
    </div>
  );
}
